"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTempPath = exports.VERSION_OVERRIDE_REGEX = exports.scopeToPackageName = exports.importInstallPkg = exports.installPlugin = exports.checkPlugin = exports.listAllPlugins = exports.executePluginCommand = void 0;
const node_child_process_1 = require("node:child_process");
const node_path_1 = __importDefault(require("node:path"));
const node_url_1 = __importDefault(require("node:url"));
const node_util_1 = require("node:util");
const chalk_1 = __importDefault(require("chalk"));
const debug_1 = __importDefault(require("debug"));
const package_json_1 = require("@datadog/datadog-ci-base/package.json");
const version_1 = require("../version");
const is_standalone_binary_1 = require("./is-standalone-binary");
const message_box_1 = require("./message-box");
const user_agent_1 = require("./user-agent");
// Use `DEBUG=plugins` to enable debug logs
const debug = (0, debug_1.default)('plugins');
const executePluginCommand = (instance) => __awaiter(void 0, void 0, void 0, function* () {
    const [scope, command] = instance.path;
    debug(`Executing command ${command} in plugin ${scope}`);
    try {
        const submodule = yield importPluginSubmodule(scope, command);
        debug(`Done importing plugin command`);
        const plugin = yield importPlugin(scope);
        printPluginVersion(plugin);
        const pluginCommand = Object.assign(new submodule.PluginCommand(), instance);
        return (0, user_agent_1.withPluginUserAgent)(plugin.name, plugin.version, () => pluginCommand.execute());
    }
    catch (error) {
        debug('Error in executePluginCommand:', error);
        if (isModuleNotFoundError(error)) {
            console.log();
            showPluginNotInstalledMessageBox(scope, command);
            showInstallPluginInstructions(scope);
            console.log();
        }
        else {
            console.log(chalk_1.default.bold.red('Unexpected error when executing the plugin command:\n'), error);
        }
        console.log([
            `To troubleshoot, run:`,
            `  ${chalk_1.default.bold.cyan(`datadog-ci plugin check`)} ${chalk_1.default.magenta(scope)}`,
            ...(command
                ? [`or`, `  ${chalk_1.default.bold.cyan(`datadog-ci plugin check`)} ${chalk_1.default.magenta(scope)} ${chalk_1.default.magenta(command)}`]
                : []),
            '',
        ].join('\n'));
        return 1;
    }
});
exports.executePluginCommand = executePluginCommand;
const listAllPlugins = () => {
    return Object.keys(package_json_1.peerDependencies);
};
exports.listAllPlugins = listAllPlugins;
const checkPlugin = (scope, command) => __awaiter(void 0, void 0, void 0, function* () {
    if (!isValidScope(scope)) {
        console.log([
            '',
            chalk_1.default.bold.red("This plugin is not listed as a possible peer dependency. Make sure you didn't make a typo."),
            '',
        ].join('\n'));
        return false;
    }
    if (yield (0, is_standalone_binary_1.isStandaloneBinary)()) {
        console.log([
            '',
            chalk_1.default.bold.green('The plugin is ready to be used! 🔌'),
            '',
            `${chalk_1.default.bold('Note:')} All plugins are already baked into the standalone binary.`,
            '',
        ].join('\n'));
        return true;
    }
    try {
        const plugin = yield importPlugin(scope);
        printPluginVersion(plugin);
        const module = command ? yield importPluginSubmodule(scope, command) : plugin;
        console.log([
            '',
            chalk_1.default.bold.green('The plugin is ready to be used! 🔌'),
            '',
            chalk_1.default.dim(`Contents: ${(0, node_util_1.inspect)(module, { colors: true })}`),
            '',
        ].join('\n'));
    }
    catch (error) {
        debug('Error in checkPlugin:', error);
        if (isPnpModuleNotFoundError(error) && error.pnpCode === 'MISSING_PEER_DEPENDENCY') {
            // This error is verbose and gives a lot of information about the PnP error, so we log it as is.
            console.log(error);
            console.log();
            showPnpPeerDependencyErrorMessageBox(scope);
            showInstallPluginInstructions(scope);
            console.log();
        }
        else if (isModuleNotFoundError(error)) {
            console.log();
            showPluginNotInstalledMessageBox(scope, command);
            showInstallPluginInstructions(scope);
            console.log();
        }
        else {
            console.log(chalk_1.default.bold.red('Unexpected error when checking the plugin:\n'), error);
        }
        return false;
    }
    return true;
});
exports.checkPlugin = checkPlugin;
/**
 * Installs a plugin as a dev dependency in the current project with the right package manager.
 * Plugin bundles are self-contained (all deps vendored), so only the plugin itself is needed.
 */
const installPlugin = (packageOrScope) => __awaiter(void 0, void 0, void 0, function* () {
    if (!isValidScope(packageOrScope)) {
        console.log([
            '',
            chalk_1.default.bold.red("This plugin is not listed as a possible peer dependency. Make sure you didn't make a typo."),
            '',
        ].join('\n'));
        return false;
    }
    const pluginPackage = getPackageToInstall(packageOrScope);
    const pluginDescriptor = packageDescriptor(pluginPackage.name, pluginPackage.version);
    console.log(chalk_1.default.dim(`Installing ${pluginDescriptor}...`));
    const { installPackage } = yield (0, exports.importInstallPkg)();
    const output = yield installPackage([pluginDescriptor], {
        silent: !debug.enabled,
        dev: true,
    });
    if (output.exitCode === 0) {
        console.log();
        (0, message_box_1.messageBox)('Installed plugin 🔌', 'green', [`Successfully installed ${chalk_1.default.bold(pluginDescriptor)}`]);
        console.log();
        return true;
    }
    else {
        console.log(chalk_1.default.bold.red(`Failed to install ${pluginDescriptor}! 🔌`));
        console.log('Stdout:', output.stdout);
        console.log('Stderr:', output.stderr);
        return false;
    }
});
exports.installPlugin = installPlugin;
// Wrapper function to be mocked in tests
const importInstallPkg = () => __awaiter(void 0, void 0, void 0, function* () {
    return import('@antfu/install-pkg');
});
exports.importInstallPkg = importInstallPkg;
const temporarilyInstallPluginWithNpx = (scope) => __awaiter(void 0, void 0, void 0, function* () {
    const isWindows = process.platform === 'win32';
    const pluginPackage = getPackageToInstall(scope);
    const pluginDescriptor = packageDescriptor(pluginPackage.name, pluginPackage.version);
    const emitPath = isWindows ? 'set PATH' : 'printenv PATH';
    const cmd = `npx --ignore-scripts -y -p ${pluginDescriptor} ${emitPath}`;
    debug('Using npx to install the missing plugin:', cmd);
    const output = yield new Promise((resolve, reject) => {
        (0, node_child_process_1.exec)(cmd, (error, stdout) => {
            if (error) {
                reject(error);
            }
            else {
                resolve(stdout);
            }
        });
    });
    debug('Output:', output);
    const tempPath = (0, exports.getTempPath)(output, isWindows);
    // The path should end with `node_modules/.bin`
    const nodeModulesPath = node_path_1.default.resolve(tempPath, '..');
    if (!nodeModulesPath.endsWith('node_modules')) {
        throw new Error(`Found NPX temporary path of '${tempPath}' but expected to be able to find a 'node_modules' directory by looking in '..'.`);
    }
    console.log();
    (0, message_box_1.messageBox)('Installed plugin 🔌', 'green', [
        `Successfully installed ${chalk_1.default.bold(pluginDescriptor)} into ${chalk_1.default.dim(nodeModulesPath)}`,
        '',
        `Consider installing the plugin explicitly with ${chalk_1.default.bold.cyan('datadog-ci plugin install')} ${chalk_1.default.magenta(scope)}.`,
    ]);
    console.log();
    // Make the plugin resolvable.
    patchModulePaths(nodeModulesPath);
});
const handlePluginAutoInstall = (scope) => __awaiter(void 0, void 0, void 0, function* () {
    if (!!process.env['DISABLE_PLUGIN_AUTO_INSTALL']) {
        debug('Found DISABLE_PLUGIN_AUTO_INSTALL env variable, skipping auto-install');
        return;
    }
    try {
        yield importPlugin(scope);
        debug('Auto-install check: plugin is installed, skipping installation');
    }
    catch (error) {
        debug('Error in handlePluginAutoInstall:', error);
        if (!isModuleNotFoundError(error)) {
            // Re-throw unexpected errors.
            throw error;
        }
        if (isPnpModuleNotFoundError(error) && error.pnpCode === 'MISSING_PEER_DEPENDENCY') {
            // Re-throw PnP errors.
            console.log(chalk_1.default.red(`The plugin auto-install feature is not supported with Yarn Plug'n'Play (PnP).`));
            throw error;
        }
        const pluginName = (0, exports.scopeToPackageName)(scope);
        console.log(chalk_1.default.yellowBright(`Could not find ${chalk_1.default.bold(pluginName)}. Installing...`));
        yield temporarilyInstallPluginWithNpx(pluginName);
    }
});
const printPluginVersion = (plugin) => {
    if (plugin.version !== version_1.cliVersion) {
        // CTA about syncing the plugin version with datadog-ci, but it's dimmed to not be too intrusive.
        console.log(chalk_1.default.dim(`${plugin.name} v${plugin.version} (run ${chalk_1.default.cyan('datadog-ci plugin install')} to sync with datadog-ci)`));
    }
    else {
        console.log(chalk_1.default.dim(`${plugin.name} v${plugin.version}`));
    }
};
const getInjectedPluginSubmodules = () => {
    if (typeof __getInjectedPlugins === 'undefined') {
        return undefined;
    }
    return __getInjectedPlugins().injectedPluginSubmodules;
};
const getInjectedPluginPackageJson = (scope) => {
    var _a;
    if (typeof __getInjectedPlugins === 'undefined') {
        return undefined;
    }
    return (_a = __getInjectedPlugins().injectedPluginPackageJsons) === null || _a === void 0 ? void 0 : _a[scope];
};
const importPluginSubmodule = (scope, command) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    if (!isValidScope(scope)) {
        throw new Error(`Invalid scope: ${scope}`);
    }
    // 1. Bundled: injected at build time (SEA has all plugins, NPM bundle has only the builtin ones)
    const injectedPluginSubmodules = getInjectedPluginSubmodules();
    if (injectedPluginSubmodules) {
        const injected = (_a = injectedPluginSubmodules[scope]) === null || _a === void 0 ? void 0 : _a[command];
        if (injected) {
            debug('Loading pre-bundled plugin');
            return injected;
        }
    }
    // Add current working directory's `node_modules` to the module resolution paths
    // in case the command is running in NPX.
    patchModulePaths();
    yield handlePluginAutoInstall(scope);
    const pluginPackage = `@datadog/datadog-ci-plugin-${scope}`;
    // 2. Self-contained plugin bundle (published vendored bundles)
    try {
        const bundle = require(pluginPackage);
        const submodule = (_b = bundle.commands) === null || _b === void 0 ? void 0 : _b[command];
        if (submodule) {
            debug('Loading self-contained plugin bundle');
            return submodule;
        }
    }
    catch (error) {
        debug('Plugin bundle load failed, trying submodule path:', error);
    }
    // 3. Development mode: load individual command submodule.
    const submoduleName = `@datadog/datadog-ci-plugin-${scope}/commands/${command}`;
    debug('Resolving submodule:', submoduleName);
    let submodulePath = submoduleName;
    try {
        const resolvedPath = require.resolve(submoduleName);
        const absolutePath = node_url_1.default.pathToFileURL(resolvedPath).href;
        submodulePath = absolutePath;
    }
    catch (error) {
        debug(`Could not require.resolve() the ${submoduleName} submodule: ${error}`);
    }
    debug('Importing submodule:', submodulePath);
    return (yield import(submodulePath));
});
const scopeToPackageName = (scope) => {
    if (scope.match(/^@datadog\/datadog-ci-plugin-[a-z-]+$/)) {
        return scope;
    }
    return `@datadog/datadog-ci-plugin-${scope}`;
};
exports.scopeToPackageName = scopeToPackageName;
const patchModulePaths = (preferredPath) => {
    const workingDirNodeModules = node_path_1.default.join(process.cwd(), 'node_modules');
    process.env['NODE_PATH'] = [process.env['NODE_PATH'], workingDirNodeModules, preferredPath]
        .filter(Boolean)
        .join(node_path_1.default.delimiter);
    require('module').Module._initPaths();
    debug('Module resolution paths set to:', process.env['NODE_PATH']);
};
const isValidScope = (scope) => {
    return (0, exports.scopeToPackageName)(scope) in package_json_1.peerDependencies;
};
/**
 * @example "1.2.3"
 * @example "file:./artifacts/@datadog-datadog-ci-base-20.tgz"
 */
exports.VERSION_OVERRIDE_REGEX = /^(\d+\.\d+\.\d+|file:\.\/[a-zA-Z0-9.\-/@]+)$/;
const getPackageToInstall = (scope) => {
    const pluginName = (0, exports.scopeToPackageName)(scope);
    const pluginVersionOverride = process.env['PLUGIN_INSTALL_VERSION_OVERRIDE'];
    if (pluginVersionOverride && !exports.VERSION_OVERRIDE_REGEX.test(pluginVersionOverride)) {
        throw new Error(`Invalid PLUGIN_INSTALL_VERSION_OVERRIDE value: ${pluginVersionOverride}`);
    }
    const pluginVersion = pluginVersionOverride !== null && pluginVersionOverride !== void 0 ? pluginVersionOverride : version_1.cliVersion;
    return {
        name: pluginName,
        version: pluginVersion,
    };
};
const importPlugin = (scope) => __awaiter(void 0, void 0, void 0, function* () {
    var _c;
    const packageNameMatch = scope.match(/^@datadog\/datadog-ci-plugin-([a-z-]+)$/);
    const normalizedScope = (_c = packageNameMatch === null || packageNameMatch === void 0 ? void 0 : packageNameMatch[1]) !== null && _c !== void 0 ? _c : scope;
    const injectedPackageJson = getInjectedPluginPackageJson(normalizedScope);
    if (injectedPackageJson) {
        return injectedPackageJson;
    }
    // Use `require()` instead of `await import()` to avoid `ERR_IMPORT_ATTRIBUTE_MISSING` due to missing `{with: {type: 'json'}}`.
    // This is only supported with `--module` set to `esnext`, `node16`, or `nodenext`.
    return extractPackageJson(require(`@datadog/datadog-ci-plugin-${normalizedScope}/package.json`));
});
const extractPackageJson = (content) => {
    if (typeof content !== 'object' || !content) {
        throw new Error('Invalid package.json: not an object');
    }
    if (!('name' in content) || typeof content.name !== 'string') {
        throw new Error('Invalid package.json: missing name');
    }
    if (!('version' in content) || typeof content.version !== 'string') {
        throw new Error('Invalid package.json: missing version');
    }
    const { name, version } = content;
    return {
        name,
        version,
    };
};
const packageDescriptor = (name, version) => `${name}@${version}`;
const showPluginNotInstalledMessageBox = (scope, command) => {
    const packageName = `@datadog/datadog-ci-plugin-${scope}`;
    if (command) {
        (0, message_box_1.messageBox)('Plugin not installed 🔌', 'red', [
            `The ${chalk_1.default.cyan(`datadog-ci ${scope} ${command}`)} command could not be found.`,
            `To use this command, please install ${chalk_1.default.bold.magenta(packageName)} alongside datadog-ci.`,
        ]);
    }
    else {
        (0, message_box_1.messageBox)('Plugin not installed 🔌', 'red', [
            `The ${chalk_1.default.bold.magenta(packageName)} package could not be found.`,
            `To use the any command in this plugin, please install it alongside datadog-ci.`,
        ]);
    }
};
const showPnpPeerDependencyErrorMessageBox = (scope) => {
    const packageName = `@datadog/datadog-ci-plugin-${scope}`;
    (0, message_box_1.messageBox)("Yarn Plug'n'Play (PnP) error 🔌", 'red', [
        `Yarn Plug'n'Play (PnP) detected that ${chalk_1.default.bold.magenta(packageName)} was not installed alongside datadog-ci.`,
    ]);
};
const showInstallPluginInstructions = (scope) => {
    const packageName = `@datadog/datadog-ci-plugin-${scope}`;
    console.log([
        '',
        `You can install the plugin using:`,
        `  ${chalk_1.default.bold.cyan('datadog-ci plugin install')} ${chalk_1.default.magenta(scope)}`,
        `or`,
        `  ${chalk_1.default.bold.cyan('datadog-ci plugin install')} ${chalk_1.default.magenta(packageName)}`,
        '',
    ].join('\n'));
};
const isModuleNotFoundError = (error) => {
    var _a;
    return (error instanceof Error &&
        ['MODULE_NOT_FOUND', 'ERR_MODULE_NOT_FOUND'].includes((_a = error.code) !== null && _a !== void 0 ? _a : ''));
};
const isPnpModuleNotFoundError = (error) => {
    return isModuleNotFoundError(error) && error.pnpCode !== undefined;
};
const NPX_PATH_REGEX = /\.npm\/_npx\//;
const NPX_PATH_WIN_REGEX = /\\npm[-\\]+cache\\_npx\\/;
/**
 * Find where NPX just installed the package.
 *
 * https://github.com/geelen/npx-import/blob/8a1e17ca4f88981b11be5090e20871f8704166b8/src/index.ts#L221-L250
 */
const getTempPath = (stdout, isWindows) => {
    if (isWindows) {
        const paths = stdout
            .replace(/^PATH=/i, '')
            .replace(/\\r\\n/g, ';')
            .split(';');
        const tempPath = paths.find((p) => NPX_PATH_WIN_REGEX.exec(p));
        if (!tempPath) {
            const list = paths.map((p) => ` - ${p}`).join('\n');
            throw new Error(`Failed to find temporary install directory. Looking for paths matching '\\npm-cache\\_npx\\' in:\n${list}`);
        }
        return tempPath;
    }
    else {
        const paths = stdout.split(':');
        const tempPath = paths.find((p) => NPX_PATH_REGEX.exec(p));
        if (!tempPath) {
            const list = paths.map((p) => ` - ${p}`).join('\n');
            throw new Error(`Failed to find temporary install directory. Looking for paths matching '/.npm/_npx/' in:\n${list}`);
        }
        return tempPath;
    }
};
exports.getTempPath = getTempPath;
//# sourceMappingURL=plugin.js.map